// Pending checkout intent persisted across the sign-up / onboarding flow.
//
// When a logged-out visitor clicks "Subscribe to <tier>" on /pricing we save
// their selection here, send them to /auth?mode=signup, and after they
// complete onboarding the <PendingCheckoutResumer /> mounted in App.tsx
// reads this back and auto-starts the Stripe Checkout session for the same
// tier + billing period.
//
// sessionStorage (not localStorage) is intentional — the intent should die
// with the tab so a half-finished flow can never resurrect days later.

import type { BillingPeriod } from '@/hooks/useCreateCheckoutSession';

const KEY = 'diy:pendingCheckout';

export interface PendingCheckout {
  tierId: string;
  billingPeriod: BillingPeriod;
  // Defensive timestamp — anything older than 1 hour is treated as stale.
  savedAt: number;
}

const MAX_AGE_MS = 60 * 60 * 1000;

export function savePendingCheckout(input: Omit<PendingCheckout, 'savedAt'>): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ ...input, savedAt: Date.now() } satisfies PendingCheckout),
    );
  } catch {
    // Storage disabled / quota — silently skip; the user can re-click on /pricing.
  }
}

export function consumePendingCheckout(): PendingCheckout | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    const parsed = JSON.parse(raw) as PendingCheckout;
    if (!parsed?.tierId || !parsed?.billingPeriod) return null;
    if (Date.now() - (parsed.savedAt ?? 0) > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

// #1914 Phase 3 — Read the pending checkout WITHOUT clearing it.
//
// `<PendingCheckoutResumer />` (mounted in App.tsx) still owns the
// consume — it fires post-signup and auto-starts Stripe Checkout. The
// Phase 3 OnboardingFlow pre-skip useEffect needs the same intent
// earlier (mid-onboarding) to derive the persona and skip ProfileStep
// + IntentStep, but MUST NOT consume, or the resumer would have
// nothing to act on.
export function peekPendingCheckout(): PendingCheckout | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingCheckout;
    if (!parsed?.tierId || !parsed?.billingPeriod) return null;
    if (Date.now() - (parsed.savedAt ?? 0) > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingCheckout(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}